import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useStore } from '../context/StoreContext.jsx';
import { api } from '../services/api-client.js';
import { ProductCard } from '../components/ProductCard.jsx';
import { LoadingSpinner } from '../components/LoadingSpinner.jsx';

export function HomePage() {
  const { language, t } = useLanguage();
  const { storeConfig } = useStore();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    async function fetchData() {
      try {
        const [prodRes, catRes] = await Promise.all([
          api.getProducts({ perPage: 4, in_stock: true }),
          api.getCategories(),
        ]);

        if (isMounted) {
          setFeaturedProducts(prodRes.data || []);
          setCategories(catRes.data || []);
        }
      } catch (err) {
        console.error('Failed to load homepage data:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)', padding: '3.5rem 0' }}>
        <div className="container" style={{ textAlign: 'center', maxWidth: '800px' }}>
          <span style={{ color: 'var(--color-primary)', fontWeight: 700, fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {language === 'ar' ? storeConfig.tagline.ar : storeConfig.tagline.fr}
          </span>
          <h1 style={{ fontSize: '2.5rem', marginTop: '0.75rem', marginBottom: '1.25rem' }}>
            {t('heroTitle')}
          </h1>
          <p style={{ fontSize: '1.125rem', marginBottom: '2rem' }}>
            {t('heroSubtitle')}
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/catalogue" className="btn btn-primary" style={{ padding: '0.75rem 1.75rem' }}>
              {t('exploreCatalog')} →
            </Link>
            <Link to="/suivi" className="btn btn-outline" style={{ padding: '0.75rem 1.75rem' }}>
              {t('orderTracking')}
            </Link>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      {categories.length > 0 && (
        <section className="container" style={{ marginTop: '3rem' }}>
          <h2 style={{ marginBottom: '1.5rem' }}>{t('allCategories')}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/catalogue?category=${cat.slug}`}
                style={{
                  backgroundColor: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  padding: '1.25rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.25rem',
                  transition: 'border-color 0.15s ease',
                }}
              >
                <strong style={{ color: 'var(--color-text-main)' }}>
                  {language === 'ar' ? cat.name_ar : cat.name_fr}
                </strong>
                <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                  {language === 'ar' ? cat.description_ar : cat.description_fr}
                </span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured Products */}
      <section className="container" style={{ marginTop: '3.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1.5rem' }}>
          <h2>{t('sortNewest')}</h2>
          <Link to="/catalogue" style={{ color: 'var(--color-primary)', fontWeight: 600, fontSize: '0.9375rem' }}>
            {t('exploreCatalog')} →
          </Link>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="products-grid">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
